<template>
    <cod-dialog
        :show.sync="showDialog"
        :drawer="drawer"
        :loading="busy"
        max-width="630px"
    >
        <!-- Navigation Drawer -->
        <div slot="drawer" class="drawer-wrapper">
            <template v-if="drawer == 'sales'">
                <v-toolbar
                    class="wfull"
                    color="#1565c0"
                    absolute
                    dark
                    flat
                >
                    <v-btn icon @click="drawer = null">
                        <v-icon>fal fa-arrow-left</v-icon>
                    </v-btn>

                    <v-toolbar-title>
                        {{ $t('staff.sales_report') }}
                    </v-toolbar-title>

                    <v-spacer />

                    <v-btn
                        text
                        dark
                        class="minw100"
                        @click="togglePrint"
                    >
                        <v-icon class="mr-3">fal fa-print</v-icon>
                        {{ $t('general.print') }}
                    </v-btn>
                </v-toolbar>

                <div class="drawer-content">
                    <v-slide-y-transition>
                        <div
                            v-if="initPrint && enableReceiptPrinter"
                            class="d-flex align-center flex-column"
                        >
                            <v-checkbox
                                v-model="useReceiptPrinter"
                                :label="$t('general.print_on_receipt_printer')"
                            />

                            <div>
                                <v-btn
                                    class="mx-2"
                                    color="primary"
                                    outlined
                                    @click="initPrint = false"
                                >
                                    <v-icon class="mr-3">fal fa-times</v-icon>
                                    {{ $t('general.cancel') }}
                                </v-btn>

                                <v-btn
                                    class="mx-2"
                                    color="primary"
                                    depressed
                                    @click="printReport(useReceiptPrinter)"
                                >
                                    <v-icon class="mr-3">fal fa-check</v-icon>
                                    {{ $t('general.print') }}
                                </v-btn>
                            </div>
                        </div>
                    </v-slide-y-transition>

                    <div class="px-5 py-2" v-html="reportHtml"></div>
                </div>
            </template>
        </div>

        <!-- Header -->
        <div slot="header">
            {{ isAllegra ? $tc('general.staff', 2) : $tc('general.waiter', 2) }}
        </div>

        <!-- Content -->
        <div slot="content" class="staff-content">
            <div
                v-if="noncashOnly"
                class="users-container"
            >
                <template v-for="(stats, index) in statistics">
                    <div
                        :key="stats.user.id"
                        class="user-panel-title px-6 py-4"
                        @click="showSalesReport(stats.user)"
                    >
                        {{ stats.user.name }}
                    </div>

                    <v-divider class="ma-0" />
                </template>
            </div>

            <v-expansion-panels
                v-else
                v-model="userPanel"
                class="users-container"
                :readonly="!allows('allow_staff')"
                accordion
            >
                <v-expansion-panel
                    v-for="(stats, index) in statistics"
                    :key="stats.user.id"
                    :class="{ single: !allows('allow_staff') }"
                >
                    <v-expansion-panel-header
                        class="user-panel-title"
                        :expand-icon="allows('allow_staff') ? 'fal fa-chevron-down' : ''"
                    >
                        {{ stats.user.name }}
                    </v-expansion-panel-header>

                    <v-expansion-panel-content v-if="userPanel === index">
                        <template v-if="tenant.venue.settings.user_deposit_enabled">
                            <div class="d-flex px-2">
                                <div>
                                    <div class="text-h6">{{ $t('general.user_deposit') }}: {{ stats.user.getDeposit().toFormat() }}</div>
                                    <div>{{ $t('general.user_deposit_description', { name: stats.user.name }) }}</div>
                                </div>

                                <v-spacer />

                                <v-btn
                                    v-if="allows('allow_staff')"
                                    text
                                    @click="$emit('update-deposit', stats.user)"
                                >
                                    {{ $t('general.add') }}
                                </v-btn>
                            </div>

                            <v-divider class="mx-2" />
                        </template>

                        <div class="d-flex px-2">
                            <div class="error--text text-h6">
                                {{ $t('general.debt') }}: {{ stats.user.getDebt().toFormat() }}
                            </div>

                            <v-spacer />

                            <v-btn
                                v-if="isClearDebtAllowed(stats.user)"
                                color="error"
                                text
                                :loading="busy"
                                :disabled="stats.user.getDebt().isZero() || busy"
                                @click="clearDebt(stats.user)"
                            >
                                {{ $t('staff.clear_debt') }}
                            </v-btn>
                        </div>

                        <v-divider class="mx-2" />

                        <v-row no-gutters>
                            <v-col cols="12" sm="4" class="px-2">

                                <div v-if="tenant.venue.settings.user_deposit_enabled" class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.cash.deposit') }}</span>
                                    <span>{{ stats.user.getDeposit().toFormat('0.00') }}</span>
                                </div>

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.cash.sold') }}</span>
                                    <span>{{ stats.cash.sold }}</span>
                                </div>

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.cash.refunded') }}</span>
                                    <span>{{ stats.cash.refunded }}</span>
                                </div>

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.cash.debt_cleared') }}</span>
                                    <span>{{ stats.user.getCharged().multiply(-1).toFormat('0.00') }}</span>
                                </div>

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.cash.debt') }}</span>
                                    <span>{{ stats.user.getDebt().toFormat('0.00') }}</span>
                                </div>

                                <v-divider class="d-flex d-sm-none mx-0" />

                            </v-col>

                            <v-col cols="12" sm="5" class="px-2">

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.payments.cash') }}</span>
                                    <span>{{ stats.payments.cash }}</span>
                                </div>

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.payments.credit_card') }}</span>
                                    <span>{{ stats.payments.credit_card }}</span>
                                </div>

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.payments.invoice') }}</span>
                                    <span>{{ stats.payments.invoice }}</span>
                                </div>

                                <div v-if="locale === 'me'" class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.payments.credit_card_business') }}</span>
                                    <span>{{ stats.payments.credit_card_business }}</span>
                                </div>

                                <div v-if="locale === 'sr'" class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.payments.check') }}</span>
                                    <span>{{ stats.payments.check }}</span>
                                </div>

                                <div v-if="locale === 'sr'" class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.payments.voucher') }}</span>
                                    <span>{{ stats.payments.voucher }}</span>
                                </div>

                                <div v-if="locale === 'sr'" class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.payments.mobile_money') }}</span>
                                    <span>{{ stats.payments.mobile_money }}</span>
                                </div>

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.payments.other_noncash') }}</span>
                                    <span>{{ stats.payments.other_noncash }}</span>
                                </div>

                                <v-divider class="mx-0 my-1" />

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.payments.advance') }}</span>
                                    <span>{{ stats.payments.advance }}</span>
                                </div>

                                <v-divider class="d-flex d-sm-none mx-0" />

                            </v-col>

                            <v-col cols="12" sm="3" class="px-2">

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.total.sold') }}</span>
                                    <span>{{ stats.total.sold }}</span>
                                </div>

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.total.refunded') }}</span>
                                    <span>{{ stats.total.refunded }}</span>
                                </div>

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('reports.body.sales.stats.total.unpaid') }}</span>
                                    <span>{{ stats.total.unpaid }}</span>
                                </div>

                            </v-col>
                        </v-row>

                        <div class="d-flex justify-content-center mt-4">
                            <v-btn
                                color="primary"
                                outlined
                                @click="showSalesReport(stats.user)"
                            >
                                {{ $t('staff.sales_report') }}
                            </v-btn>
                        </div>

                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
    </cod-dialog>
</template>

<script>
import CodDialog from '@/components/CodDialog';
import User from '@/models/User';
import Order from '@/models/Order';
import Shift from '@/models/Shift';
import Report from '@/models/Report';
import CashRegister from '@/models/CashRegister';
import Terminal from '@/models/Terminal';

export default {
    components: {
        CodDialog,
    },

    props: {
        show: {
            type: Boolean,
            default: false
        }
    },

    data: () => ({
        showDialog: false,
        statistics: [],
        orders: [],
        drawer: null,
        userPanel: 0,

        report: null,
        reportHtml: null,

        initPrint: false,
        receiptPrinter: null,
        useReceiptPrinter: false,

        busy: false,
        terminal: null
    }),

    watch: {
        async show(val) {
            this.showDialog = val;
            this.drawer = null;

            if(val) {
                this.useReceiptPrinter = this.enableReceiptPrinter;
                this.receiptPrinter = this.$printService.getAssignedPrinter('receipt');

                const curShift = Shift.current();
                this.orders = curShift ? curShift.getOrders() : [];
                this.userPanel = 0;
                this.createStats();
                this.terminal = await Terminal.current()
            }
        },

        showDialog(val) {
            this.$emit('update:show', val);
        },

        userPanel(val) {
            if(Number.isInteger(val)) {
                this.createStatsForUser(val)
            }
        }
    },

    computed: {
        enableReceiptPrinter() {
            return !!this.receiptPrinter && this.device.platform !== 'web'
        },

        noncashOnly() {
            return !!this.tenant.venue?.settings?.noncash_only
        },
    },

    methods: {
        createStats() {
            if(!this.user) return;

            this.statistics = [];

            const users = this.allows('allow_staff') ? User.all() : [User.find(this.user.id)]

            users.forEach(user => {
                this.statistics.push({
                    user: user.$duplicate(),
                    cash: {
                        sold: '0.00',
                        refunded: '0.00',
                        deposit: '0.00',
                        debt_cleared: '0.00',
                        debt: '0.00',
                    },
                    payments: {
                        cash: '0.00',
                        credit_card: '0.00',
                        invoice: '0.00',
                        credit_card_business: '0.00',
                        check: '0.00',
                        voucher: '0.00',
                        mobile_money: '0.00',
                        other_noncash: '0.00',
                        advance: '0.00',
                    },
                    total: {
                        sold: '0.00',
                        refunded: '0.00',
                        unpaid: '0.00',
                    }
                });
            });

            this.createStatsForUser(0);
        },

        createStatsForUser(index) {
            const user = this.statistics[index].user;
            const filteredOrders = this.orders.filter(order => {
                const userId = Number(order.getFlag('clearDebtForUser')[0]) || order.user_id
                return userId == user.id
            })

            const stats = Order.generateStats(filteredOrders)

            stats.cash.deposit = user.getDeposit().toFormat('0.00');
            stats.cash.debt_cleared = user.getCharged().multiply(-1).toFormat('0.00');
            stats.cash.debt = user.getDebt().toFormat('0.00');

            this.statistics[index] = { ...this.statistics[index], ...stats }
        },

        async clearDebt(user) {
            this.busy = true;

            const debt = user.getDebt();
            const result = await user.clearDebt();

            this.busy = false;

            if(!result.success) {
                this.$snackbar(this.$t('staff.user_debt_clear_error'), 'error');
                return;
            }

            this.$snackbar(this.$t('staff.user_debt_cleared'), 'success');

            const dateFormat = ['me', 'sr', 'ba'].includes(locale) ? 'D MMM HH:mm' : 'D MMM HH:mm'

            const printText = `\n${this.$t('staff.user_debt_cleared')}\n\n` +
                `${user.name}\n` +
                `${this.$t('general.time')}: ${moment().format(dateFormat)}\n` +
                `${this.$t('general.debt')}: ${debt.toFormat('0.00')}\n\n\n`;

            const printHtml = printText.replace(/(?:\r\n|\r|\n)/g, '<br>');

            this.$printService.printCustom(printText, printHtml)
        },

        async showSalesReport(user) {
            this.report = await this.createReport(user)
            this.reportHtml = this.report.getPrintable().toHtml('report').join('')
            this.drawer = 'sales'
        },

        async createReport(user) {
            const curShift = Shift.current()
            const curCashRegister = CashRegister.current()

            return await Report.create({
                type: 'sales_by_user_detailed',
                startTime: curShift.getOpenedAt().format('YYYY-MM-DD HH:mm:ss'),
                endTime: moment().format('YYYY-MM-DD HH:mm:ss'),
                cashRegisterId: curCashRegister.id,
                orders: this.orders,
                filterOrders: {
                    userId: user.id
                }
            });
        },

        togglePrint() {
            if(!this.enableReceiptPrinter) {
                this.printReport(false)
                return
            }

            if(this.device.platform === 'android') {
                this.printReport(true)
                return
            }

            this.initPrint = !this.initPrint
        },

        printReport(useReceiptPrinter) {
            this.initPrint = false

            this.$printService.printReport(this.report, {
                type: this.report.type,
                choosePrinter: !useReceiptPrinter
            })
        },

        isClearDebtAllowed(user) {
            if(this.terminal?.isOrderingDevice()) return false;

            if(this.allows('allow_clear_debt_all')) return true;

            return this.user.id === user.id && this.allows('allow_clear_debt_self');
        }
    }
};
</script>

<style lang="scss" scoped>
.staff-content {
    min-height: 600px;

    .loading {
        margin: -1px;
    }

    .users-container {
        .user-panel-title {
            color: #333;
            cursor: pointer;

            &:hover {
                background-color: #f3f3f3;
            }
        }

        .v-expansion-panel {
            &:before {
                box-shadow: none;
            }

            &.v-expansion-panel--active {
                background-color: #eee;

                &.single {
                    background-color: white;
                }
            }
        }
    }
}

.drawer-wrapper {
    height: 100%;
    width: 100%;

    .drawer-content {
        max-height: 100%;
        overflow-y: auto;
        padding-top: 64px;
    }
}

.v-divider {
    margin: 15px;
}

.v-menu {
    z-index: 1000;
}
</style>
